import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { ApplicationDataState } from './application.model';
import { EnvironmentService } from '../helpers/environment.service';
import { initialValue } from './application-store.init';

@Injectable({
  providedIn: 'root'
})
export class ApplicationStore {
  private readonly environmentService: EnvironmentService = inject(EnvironmentService);

  private readonly state = new BehaviorSubject<ApplicationDataState>(
    this.environmentService.getEnvironment().production ? {} : initialValue
  );

  get value(): ApplicationDataState {
    return this.state.value;
  }

  constructor() {}

  instant<F extends keyof ApplicationDataState>(field: F): ApplicationDataState[F] {
    return this.state.value[field];
  }

  query<F extends keyof ApplicationDataState>(field: F): Observable<ApplicationDataState[F]> {
    return this.state.pipe(map(state => state[field]));
  }

  patch<F extends keyof ApplicationDataState>(field: F, data: Partial<ApplicationDataState[F]>): void {
    const state = { ...this.state.value };
    state[field] =
      typeof state[field] === 'object'
        ? ({ ...(state[field] as object), ...(data as object) } as ApplicationDataState[F])
        : (data as ApplicationDataState[F]);
    this.state.next(state);
  }

  update<F extends keyof ApplicationDataState>(field: F, data: ApplicationDataState[F]): void {
    const state = { ...this.state.value };
    state[field] = data;
    this.state.next(state);
  }

  emptyAllStore(): void {
    Object.keys(this.state.value).forEach(field => {
      this.state.value[field as keyof ApplicationDataState] = null;
    });
  }

  reset(value: ApplicationDataState | null = null): void {
    this.state.next(value || (this.environmentService.getEnvironment().production ? {} : initialValue));
  }

  isEmpty(): boolean {
    return !Object.keys(this.state.value).some(field => this.state.value[field as keyof ApplicationDataState]);
  }
}
